import { CurationItem } from "../../types";
import { DEFAULT_SMART_LIST_ADAPTER_ID, DEFAULT_SMART_LIST_SORT_OPTION } from "../../constants";
import { SMART_LIST_TEMPLATE_CHOICES } from "../../../constants/smartListOptions";
import { callCurationProxy } from "../backend/backendClient";
import {
    DEFAULT_SMART_LIST_TEMPLATE_ID,
    parseJsonOrThrow,
    readTextOrThrow,
} from "../lists/helpers";

/**
 * Fetch the latest version of a content item from Vimond API
 * @param listId - The list ID to fetch
 * @returns Promise<CurationItem> - The complete item with all metadata
 */
export const fetchLatestItem = async (listId: string): Promise<CurationItem> => {
    if (!listId || listId.trim().length === 0) {
        throw new Error("fetchLatestItem requires a valid listId");
    }

    const responseText = await readTextOrThrow(
        await callCurationProxy({
            path: `/list/${listId}`,
            method: "GET",
        }),
        "fetchLatestItem",
    );

    if (responseText.trim().toLowerCase().startsWith("<!doctype html")) {
        throw new Error(
            "Authentication Failed: The API returned a login page. Please verify your authentication cookies.",
        );
    }

    const payload = parseJsonOrThrow<Record<string, { result?: CurationItem }>>(
        responseText,
        "fetchLatestItem",
    );
    const listData = payload[listId];

    if (!listData?.result) {
        throw new Error(`fetchLatestItem: API returned invalid item structure for ${listId}`);
    }

    return listData.result;
};

/**
 * Smart list configuration parsed from CurationItem
 */
export interface SmartListConfig {
    templateId: string;
    adapterId: number;
    sortOption: string;
    genre?: string;
    labels?: string[];
}

/**
 * Parse smart list configuration from a CurationItem
 * @param item - The curation item to parse
 * @returns SmartListConfig | null - Parsed config or null if not a smart list
 */
export const parseSmartListConfig = (item: CurationItem): SmartListConfig | null => {
    const normalizedContentType = (item.contentType ?? "").toLowerCase();

    if (normalizedContentType !== "list_smart" || !item.smartList) {
        return null;
    }

    const labelsString = item.smartList.values?.labels ?? "";
    const labelArray = labelsString
        .split(",")
        .map((label) => label.trim().toLowerCase())
        .filter((label) => label.length > 0);

    const resolvedTemplateId = item.templateId ? String(item.templateId) : undefined;
    const templateChoice = resolvedTemplateId
        ? SMART_LIST_TEMPLATE_CHOICES.find((choice) => choice.templateId === resolvedTemplateId)
        : undefined;

    const requiresGenre = templateChoice?.requiresGenre ?? labelArray.length === 1;
    const requiresLabels = templateChoice?.requiresLabels ?? !requiresGenre;

    let genre: string | undefined;
    let labels: string[] | undefined;

    if (requiresGenre && labelArray.length > 0) {
        [genre] = labelArray;
    }

    if (requiresLabels && labelArray.length > 0) {
        labels = labelArray;
    }

    return {
        templateId: String(item.templateId ?? DEFAULT_SMART_LIST_TEMPLATE_ID),
        adapterId: item.adapterId ?? DEFAULT_SMART_LIST_ADAPTER_ID,
        sortOption: item.smartList.values?.sort ?? DEFAULT_SMART_LIST_SORT_OPTION,
        genre,
        labels,
    };
};
